// Phase 29.4 · Focus-SKU admin editor. Role-gated to SUPER_ADMIN + MANAGEMENT
// via the route wrapper; this component assumes the caller is authorized.
import { useCallback, useEffect, useState } from "react";
import { Loader2, Save, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import PageHeader from "@/components/PageHeader";

export default function FocusSkusAdmin() {
  const [current, setCurrent] = useState([]); // [{sku_id, sku_code, name}]
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/focus-skus");
      setCurrent(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runSearch = async (val) => {
    setQ(val);
    if (val.length < 2) { setResults([]); return; }
    try {
      const { data } = await api.get("/skus", {
        params: { q: val, page_size: 30 } });
      // Phase 29.4.1 · match ONLY sku_code + name (case-insensitive
      // substring). The backend /skus q= filter may also match sku_id
      // (uuid tokens like "sku-*"), so we re-filter client-side to strip
      // any id-only matches.
      const needle = val.toLowerCase();
      const filtered = (data.items || []).filter((s) => {
        const code = (s.sku_code || "").toLowerCase();
        const name = (s.name || "").toLowerCase();
        return code.includes(needle) || name.includes(needle);
      }).slice(0, 15);
      setResults(filtered);
    } catch (e) { /* silent */ }
  };

  const add = (sku) => {
    if (current.find((c) => c.sku_id === sku.id)) return;
    setCurrent([...current, {
      sku_id: sku.id, sku_code: sku.sku_code, name: sku.name,
    }]);
    setResults([]); setQ("");
  };

  const remove = (id) => setCurrent(current.filter((c) => c.sku_id !== id));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/focus-skus", {
        focus_sku_ids: current.map((c) => c.sku_id),
      });
      toast.success(`Saved ${current.length} focus SKU(s)`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="focus-skus-admin-page">
      <PageHeader
        breadcrumbs={[
          { label: "Settings", to: "/" },
          { label: "Focus SKUs" },
        ]}
        title="Focus SKU curation"
        subtitle="Set the SKUs surfaced under 'Focus SKU performance' on every Retailer Dashboard."
        testid="focus-skus-header"
      />

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4 space-y-2">
          <div className="text-sm font-semibold text-neutral-900">Add SKUs</div>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-3 text-neutral-400" />
            <Input placeholder="Search by SKU code or name…"
                   value={q}
                   onChange={(e) => runSearch(e.target.value)}
                   className="pl-9"
                   data-testid="focus-skus-search" />
          </div>
          {results.length > 0 && (
            <div className="border border-neutral-200 rounded-lg divide-y max-h-56 overflow-y-auto">
              {results.map((s) => (
                <button key={s.id}
                        onClick={() => add(s)}
                        className="w-full text-left px-3 py-2 hover:bg-neutral-50 text-sm"
                        data-testid={`focus-skus-add-${s.id.slice(0, 8)}`}>
                  <b>{s.sku_code}</b> · {s.name}
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-neutral-200 rounded-2xl"
            data-testid="focus-skus-current">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-neutral-900">
              Current focus set ({current.length})
            </div>
            <Button size="sm" onClick={save} disabled={saving}
                    className="tulsi-gradient text-white h-9"
                    data-testid="focus-skus-save">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </div>
          {loading && <Loader2 className="h-5 w-5 animate-spin mx-auto my-4 text-neutral-400" />}
          {!loading && current.length === 0 && (
            <div className="text-xs text-neutral-500 py-4 text-center">
              No focus SKUs curated. The dashboard will fall back to the top-10
              SKUs by system-wide 90-day quantity.
            </div>
          )}
          {current.map((c) => (
            <div key={c.sku_id}
                 className="flex items-center gap-2 py-1.5 border-b last:border-b-0 border-neutral-100"
                 data-testid={`focus-skus-row-${c.sku_id.slice(0, 8)}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-neutral-400">{c.sku_code}</div>
                <div className="text-sm text-neutral-900 truncate">{c.name}</div>
              </div>
              <button onClick={() => remove(c.sku_id)}
                      className="text-red-600 p-1"
                      data-testid={`focus-skus-remove-${c.sku_id.slice(0, 8)}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
